import {
  type LeaderboardPeriod,
  type LeaderboardScope,
  VARIANTS,
  type Variant,
} from "@app/protocol";
import { useT } from "../i18n/context";
import { GAMES } from "./GameTiles";

/** The leaderboard's three pickers (design "Liderlik tablosu"); the screen keeps the state. */

const PERIODS: readonly LeaderboardPeriod[] = ["week", "month", "all"];
const SCOPES: readonly LeaderboardScope[] = ["all", "guild"];

/** Week | Month | All time: three equal tabs in a pill (design). */
export function PeriodTabs({
  period,
  onPick,
}: {
  period: LeaderboardPeriod;
  onPick: (p: LeaderboardPeriod) => void;
}) {
  const { t } = useT();
  return (
    <div
      role="tablist"
      aria-label={t("leaderboard.periods")}
      className="grid grid-cols-3 gap-1 rounded-pill border-[1.5px] border-ink/28 bg-panel-strong p-1"
    >
      {PERIODS.map((p) => (
        <button
          key={p}
          type="button"
          role="tab"
          aria-selected={p === period}
          className={`rounded-pill py-[9px] text-center text-[13px] font-extrabold ${p === period ? "bg-gold text-gold-ink" : "text-ink hover:brightness-[1.4]"}`}
          onClick={() => onPick(p)}
        >
          {t(`leaderboard.period.${p}`)}
        </button>
      ))}
    </div>
  );
}

/** Everyone or only your own Discord server; hidden until the server is verified (slice 0023). */
export function ScopeTabs({
  scope,
  onPick,
}: {
  scope: LeaderboardScope;
  onPick: (s: LeaderboardScope) => void;
}) {
  const { t } = useT();
  return (
    <div
      role="tablist"
      aria-label={t("leaderboard.who")}
      className="grid grid-cols-2 gap-1 rounded-pill border-[1.5px] border-ink/28 bg-panel-strong p-1"
    >
      {SCOPES.map((s) => (
        <button
          key={s}
          type="button"
          role="tab"
          aria-selected={s === scope}
          className={`rounded-pill py-[9px] text-center text-[13px] font-extrabold ${s === scope ? "bg-gold text-gold-ink" : "text-ink hover:brightness-[1.4]"}`}
          onClick={() => onPick(s)}
        >
          {t(`leaderboard.scope.${s}`)}
        </button>
      ))}
    </div>
  );
}

/** The games in a scrolling row; the chosen one carries its suit (design). */
export function GameChips({ variant, onPick }: { variant: Variant; onPick: (v: Variant) => void }) {
  const { t } = useT();
  return (
    <fieldset className="m-0 flex min-w-0 gap-2 overflow-x-auto border-0 p-0 pb-0.5 [scrollbar-width:none]">
      <legend className="sr-only">{t("lobby.game")}</legend>
      {VARIANTS.map((v) => {
        const on = v === variant;
        const suit = GAMES.find((g) => g.variant === v)?.suit ?? "";
        return (
          <button
            key={v}
            type="button"
            aria-pressed={on}
            className={`flex-none rounded-pill border-[1.5px] px-4 py-2 text-[13px] ${on ? "border-ink/50 bg-ink/16 font-extrabold" : "border-ink/25 bg-panel font-bold text-ink/80 hover:brightness-[1.4]"}`}
            onClick={() => onPick(v)}
          >
            {on ? <span aria-hidden="true">{`${suit} `}</span> : null}
            {t(`variant.${v}`)}
          </button>
        );
      })}
    </fieldset>
  );
}
